import type { AxiosError } from 'axios';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';

import type { ChargerFlowErrors, CreateUpdateChargerFlowBody } from '@models/chargerFlows';

import { useToast } from '@hooks/useToast';
import { createChargerFlow, updateChargerFlow } from './api';
import { useSetNavigationGuardStore } from '@features/navigationGuard/store';
import { useResetEditChargerFlowErros, useSetEditChargerFlowErrors } from './store';

export const useCreateChargerFlow = () => {
  // const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { chargerFlow: CreateUpdateChargerFlowBody }) =>
      createChargerFlow(payload.chargerFlow),
    onSuccess: (data) => {
      // Invalidate and refetch
      toast.success('Charger flow created successfully');
      if (data?.id) {
        navigate(`/dashboard/charger-flows/${data.id}/edit`);
      }
    },
    onError: (error: AxiosError) => {
      if (error.status === 400) {
        toast.error(`Duplicate name, please edit your current flow named - "New Flow"`);
        return;
      }

      toast.error(`Issue creating the charger flow: ${error.message}`);
    },
  });
};

export function useUpdateChargerFlow() {
  // const queryClient = useQueryClient();
  const toast = useToast();
  const setNavigationGuard = useSetNavigationGuardStore();
  const setEditChargerFlowErrors = useSetEditChargerFlowErrors();
  const setResetChargerFlowErrors = useResetEditChargerFlowErros();

  return useMutation({
    mutationFn: (payload: { flowId: string; chargerFlow: CreateUpdateChargerFlowBody }) =>
      updateChargerFlow(payload.flowId, payload.chargerFlow),
    onSuccess: () => {
      // Reset errors
      setResetChargerFlowErrors();
      // Invalidate and refetch
      toast.success('Charger flow updated successfully');
      setNavigationGuard(false);
    },
    onError: (error: AxiosError) => {
      const errorMessage = (error.response?.data as { message?: string })?.message;
      const errorList = (error.response?.data as { errors?: ChargerFlowErrors })?.errors;

      if (errorList) {
        setEditChargerFlowErrors(errorList);
      }

      if (errorMessage) {
        toast.error(`${errorMessage}`);
        return;
      }

      toast.error(`Issue updating the charger flow: ${error.message}`);
    },
  });
}
